// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plus,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useKeycloak } from "../../context/KeycloakProvider";
import { bookingService } from "../../services/bookingService";

const UserDashboard = () => {
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getUserInfo } = useKeycloak();

  useEffect(() => {
    fetchRecentBookings();
  }, []);

  const fetchRecentBookings = async () => {
    try {
      const userInfo = getUserInfo();
      const userId = userInfo?.sub || userInfo?.id;

      if (userId) {
        const response = await bookingService.getUserBookings(userId, {
          limit: 5,
        });
        setRecentBookings(response.data || response || []);
      }
    } catch (error) {
      console.error("Error fetching recent bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
      case "confirmed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected":
      case "canceled":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back!</h1>
        <p className="text-gray-600">
          Manage your events and bookings from your personal dashboard
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <Link
              to="/user/bookings"
              className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center"
            >
              <Calendar className="inline h-4 w-4 mr-2" />
              View My Bookings
            </Link>
            <button className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center hidden">
              <Plus className="inline h-4 w-4 mr-2" />
              Create New Booking
            </button>
            <button className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <Mail className="inline h-4 w-4 mr-2" />
              Contact Support
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Bookings
          </h3>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : recentBookings.length > 0 ? (
            <div className="space-y-3">
              {recentBookings.slice(0, 3).map((booking) => (
                <div key={booking.id} className="flex items-center space-x-3">
                  {getStatusIcon(booking.status)}
                  <span className="text-sm text-gray-600 truncate">
                    {booking.title || "Untitled Event"}
                  </span>
                </div>
              ))}
              {recentBookings.length > 3 && (
                <Link
                  to="/user/bookings"
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  View all bookings →
                </Link>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent bookings</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Bookings:</span>
              <span className="text-sm font-medium">
                {recentBookings.length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active Events:</span>
              <span className="text-sm font-medium">
                {
                  recentBookings.filter(
                    (b) => b.status === "approved" || b.status === "confirmed",
                  ).length
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Pending:</span>
              <span className="text-sm font-medium">
                {
                  recentBookings.filter(
                    (b) => b.status === "pending" || b.status === "submitted",
                  ).length
                }
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
